"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useState } from "react";
import { Star, Quote } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const testimonials = [{
  id: 1,
  author: "Idrissa Traoré",
  role: "Directeur Général, InvestBurkina",
  content: "Une approche stratégique rare sur le marché. Freelance Com & Event a su traduire notre vision institutionnelle avec une élégance et une précision méthodologique remarquables.",
  avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=256&h=256&auto=format&fit=crop",
  rating: 5
}, {
  id: 2,
  author: "Mariam Sawadogo",
  role: "Coordination Com, Action Sahel",
  content: "Leur expertise en événementiel professionnel a transformé notre forum annuel en une expérience mémorable. Une rigueur opérationnelle qui rassure les organisations exigeantes.",
  avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=256&h=256&auto=format&fit=crop",
  rating: 5
}, {
  id: 3,
  author: "Yacouba Koné",
  role: "Fondateur, FasoTech Solutions",
  content: "Un branding percutant et une stratégie digitale B2B qui nous ont permis de nous positionner immédiatement comme un acteur crédible auprès des investisseurs internationaux.",
  avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=256&h=256&auto=format&fit=crop",
  rating: 5
}, {
  id: 4,
  author: "Aminata Diallo",
  role: "Directrice, Excellence Prestige",
  content: "Collaborer avec cette agence, c'est l'assurance d'un service fluide, créatif et parfaitement aligné sur les codes du luxe et de la communication de haut niveau au Burkina.",
  avatar: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=256&h=256&auto=format&fit=crop",
  rating: 5
}];
export default function IlsNousFontConfiance() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!api) {
      return;
    }
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);
  return <section data-section-id="52788" id="paroles-de-partenaires" ref={ref} className={`py-24 bg-background text-foreground transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-foreground mb-4 tracking-tight">
            Paroles de Partenaires
          </h2>
          <p className="text-lg text-muted-foreground font-sans">
            Découvrez les témoignages des organisations et leaders qui nous font confiance pour orchestrer leur image de marque et leurs événements stratégiques.
          </p>
        </div>

        <div className="relative max-w-6xl mx-auto">
          <Carousel setApi={setApi} opts={{
          align: "start",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-4 md:-ml-6 pb-4">
              {testimonials.map((testimonial, index) => <CarouselItem key={testimonial.id} data-index={index} className="pl-4 md:pl-6 md:basis-1/2 lg:basis-1/3">
                  <div className="h-full pt-2">
                    <Card className="h-full border border-border/10 shadow-xl bg-card/40 backdrop-blur-xl hover:bg-card/60 transition-all duration-500 rounded-md overflow-hidden flex flex-col">
                      <CardContent className="flex flex-col flex-grow p-8">
                        {}
                        <div className="flex gap-1 mb-6">
                          {Array.from({
                        length: 5
                      }).map((_, i) => <Star key={i} data-index={i} className={`w-4 h-4 ${i < testimonial.rating ? "fill-primary text-primary" : "fill-muted text-muted-foreground"}`} />)}
                        </div>

                        {}
                        <Quote className="w-10 h-10 text-primary/20 mb-4" />

                        {}
                        <blockquote className="flex-grow mb-8">
                          <p className="text-lg font-serif italic text-foreground/90 leading-relaxed">
                            "{testimonial.content}"
                          </p>
                        </blockquote>

                        {}
                        <div className="flex items-center gap-4 mt-auto pt-6 border-t border-primary/10">
                          <SafeHydrate><Avatar className="w-12 h-12 border border-primary/20 shadow-sm">
                            <AvatarImage src={testimonial.avatar} alt={testimonial.author} className="object-cover" />
                            <AvatarFallback className="bg-primary/10 text-primary font-bold">
                              {testimonial.author.split(" ").map(n => n[0]).join("")}
                            </AvatarFallback>
                          </Avatar></SafeHydrate>
                          <div>
                            <div className="font-semibold text-foreground tracking-tight">
                              {testimonial.author}
                            </div>
                            <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                              {testimonial.role}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </CarouselItem>)}
            </CarouselContent>

            {}
            <div className="hidden md:block">
              <CarouselPrevious className="left-[-3rem] h-12 w-12 border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground transition-colors" />
              <CarouselNext className="right-[-3rem] h-12 w-12 border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground transition-colors" />
            </div>

            {}
            <div className="flex justify-center gap-2 mt-10">
              {Array.from({
              length: count
            }).map((_, index) => <Button key={index} data-index={index} variant="ghost" size="sm" className={`h-2 w-2 p-0 rounded-full transition-all duration-500 ${index === current ? "bg-primary w-8" : "bg-primary/20 hover:bg-primary/40"}`} onClick={() => api?.scrollTo(index)} aria-label={`Aller à la diapositive ${index + 1}`}>
                  <span className="sr-only">Slide {index + 1}</span>
                </Button>)}
            </div>
          </Carousel>
        </div>
      </div>
    </section>;
}